/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { View, StyleSheet, TouchableOpacity } from 'react-native';

import Button from '@components/form/Button';
import StatusPicker from '@components/form/StatusPicker';
import DatePicker from '@components/form/DatePicker';
import StatusBadge from '@components/global/StatusBadge';
import Image from '@components/global/Image';
import SociollaText from '@components/sociolla/SociollaText';
import Table from '@components/table/Table';
import TableMobile from '@components/table/TableMobile';
import * as Layout from '@components/screen/Layout';
import ModalPromotionRuleExport from './components/ModalPromotionRuleExport';
import MoreButton from '@components/navbar/MoreButton';

import { showPopup } from '@/store/modal';
import { isWeb } from '@global/screens';
import Colors from '@global/colors';
import { parseDate, hasNoAccess } from '@global/helpers';
import { filterAdded, paramsReseted } from '@/store/params';
import useListScreen from '@/hooks/useListScreen';
import {
  useGetPromotionRulesListQuery,
  useGetPromotionRulesCountQuery,
  useGetPromotionRulesInfiniteListInfiniteQuery,
} from '@api/listApi';

const tableStructure = {
  name: {
    head: 'Rules Name',
    body: 'name',
    textStyle: { fontFamily: 'figtree-medium' },
  },
  promotion_type: { head: 'Promo Type', body: 'promotion_type' },
  promotion_model: { head: 'Promo Model', body: 'promotion_model' },
  periode: { head: 'Periode', body: 'periode' },
  current_status: { head: 'Status', body: 'current_status', type: 'status' },
  is_active: { head: 'Is Active', body: 'is_active', type: 'status_active' },
  last_updated: { head: 'Last Updated, by', body: 'last_updated' },
};

const PROMO_MODEL_LIST = [
  { name: 'Extra Discount', value: 'extra_discount' },
  { name: 'Reguler Discount', value: 'regular_discount' },
  { name: 'Buy X Get X and Buy X Get Y', value: 'buy_x_get_x_and_buy_x_get_y' },
  { name: 'Cash Before Delivery', value: 'cash_before_delivery' },
];

const STATUS_LIST = [
  { name: 'Draft', value: 'draft' },
  { name: 'Need Approval', value: 'need_approval' },
  { name: 'Approved', value: 'approved' },
  { name: 'Rejected', value: 'rejected' },
];

const ADD_ROLES = ['superadmin', 'sales_admin', 'marketing_admin', 'general'];

// UI filter shape → API query filter (was the inline transform in doSearch):
//  - name → { $regex, $options }
//  - from_date range / promotion_model / current_status / is_active pass through
const toApiFilter = (raw = {}) => {
  const filter = {};
  const nameVal = raw.name?.$regex ?? raw.name;
  if (nameVal) {
    filter.name = { $regex: nameVal, $options: 'i' };
  }
  if (raw.from_date) {
    filter.from_date = raw.from_date;
  }
  if (raw.promotion_model) {
    filter.promotion_model = raw.promotion_model;
  }
  if (raw.current_status) {
    filter.current_status = raw.current_status;
  }
  if (
    raw.is_active !== undefined &&
    raw.is_active !== '' &&
    raw.is_active !== null
  ) {
    filter.is_active = raw.is_active;
  }
  return filter;
};

// Display-formatted row (RTK cache is frozen, so build a new object). Promo
// type/model labels come from the redux lookup maps.
const mapRow = (item, promotionTypes, promotionModels) => ({
  ...item,
  id: item._id ?? item.id,
  promotion_type: item.promotion_type
    ? promotionTypes[item.promotion_type]
    : '-',
  promotion_model: item.promotion_model
    ? promotionModels[item.promotion_model]
    : '-',
  periode:
    item.from_date || item.to_date
      ? `${
          item.from_date
            ? parseDate(item.from_date, isWeb ? 'd MMMM yyyy' : 'd MMM yyyy')
            : '-'
        } - ${
          item.to_date
            ? parseDate(item.to_date, isWeb ? 'd MMMM yyyy' : 'd MMM yyyy')
            : '-'
        }`
      : '-',
  last_updated:
    item.updated_at || item.updated_by
      ? `${item.updated_at ? parseDate(item.updated_at) : '-'} | ${
          item?.updated_by?.name ? item.updated_by.name : '-'
        }`
      : '-',
  current_status: item.current_status || 'draft',
});

const PromotionRulesScreen = (props) => {
  // ── selectors ──
  const dispatch = useDispatch();
  const userRole = useSelector((state) => state.auth.user.role);
  const promotionTypes = useSelector((state) => state.global.promotionTypes);
  const promotionModels = useSelector((state) => state.global.promotionModels);
  const showFilter = useSelector((state) => state.modal.showFilter);
  const showPromotionRuleExport = useSelector(
    (state) => state.modal.showPromotionRuleExport,
  );

  // ── local state ──
  const [draftFilter, setDraftFilter] = useState({});

  // ── derived ──
  const hasAddAccess = ADD_ROLES.includes(userRole && userRole.code);

  // ── list data ──
  const { list, count, openChild, scope, params } = useListScreen({
    listHook: useGetPromotionRulesListQuery,
    countHook: useGetPromotionRulesCountQuery,
    infiniteHook: useGetPromotionRulesInfiniteListInfiniteQuery,
    toFilter: toApiFilter,
  });

  // ── memoized values ──
  const searchValue = useMemo(() => {
    const k = params.filter?.name;
    return k && k.$regex ? k.$regex : k ?? '';
  }, [params.filter]);

  const rows = useMemo(() => {
    const raw = isWeb
      ? Array.isArray(list.data)
        ? list.data
        : []
      : (list.data?.pages ?? []).flat();
    return raw.map((item) => mapRow(item, promotionTypes, promotionModels));
  }, [list.data, promotionTypes, promotionModels]);

  // ── handlers ──
  const gotToDetail = (data) => {
    openChild('PromotionRulesDetailScreen', {
      titleScreen: data.name,
      name: data.name,
      id: data._id,
    });
  };

  const handleCreate = () => {
    openChild('PromotionRulesFormScreen');
  };

  const onExport = () => {
    dispatch(showPopup({ name: 'showPromotionRuleExport', value: true }));
  };

  const draftDateHandler = (value, bound) => {
    setDraftFilter((f) => ({
      ...f,
      from_date: { ...f.from_date, [bound]: new Date(value).toISOString() },
    }));
  };

  // ── effects ──
  useEffect(() => {
    if (isWeb) {
      return;
    }
    try {
      const showFilterMenu = () =>
        dispatch(showPopup({ name: 'showFilter', value: 'promotion-rules' }));
      const menus = [
        { type: 'function', name: 'Filter', callback: showFilterMenu },
        { type: 'function', name: 'Export', callback: onExport },
      ];
      if (!hasNoAccess(ADD_ROLES)) {
        menus.unshift({
          type: 'function',
          name: 'Create Rule',
          callback: handleCreate,
        });
      }
      props.navigation.setOptions({
        headerRight: () => <MoreButton menu={menus} {...props} />,
      });
    } catch (error) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.navigation]);

  // Seed the staged filter draft from the applied filter whenever the modal opens.
  useEffect(() => {
    if (showFilter) {
      setDraftFilter(params.filter || {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFilter]);

  // ── render ──
  return (
    <Layout.Container>
      {isWeb ? (
        <>
          <Layout.Title title="Promotion Rules">
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              {hasAddAccess ? (
                <>
                  <Button
                    type="primary"
                    rounded={true}
                    buttonStyle={{ width: 130, marginRight: 15 }}
                    title={
                      <SociollaText
                        type="medium"
                        style={{ fontSize: 14, color: Colors.white }}>
                        + Create Rules
                      </SociollaText>
                    }
                    onPress={handleCreate}
                  />
                  <Button
                    type="primary"
                    rounded={true}
                    buttonStyle={{ width: 100 }}
                    title={
                      <SociollaText
                        type="medium"
                        style={{ fontSize: 14, color: Colors.white }}>
                        Export
                      </SociollaText>
                    }
                    onPress={() => onExport()}
                  />
                </>
              ) : (
                false
              )}
            </View>
          </Layout.Title>

          <Table
            filterName={scope}
            name="promotion-rules"
            structures={tableStructure}
            data={rows}
            loading={list.isLoading}
            refetching={list.isFetching}
            hasCallback={true}
            hasNoHide={true}
            totalData={count.data}
            skip={params.skip}
            limit={params.limit}
            searchKey="name"
            searchValue={searchValue}
            searchPlaceholder="Search by name..."
            onClickRow={({ data }) => gotToDetail(data)}
          />
        </>
      ) : (
        <TableMobile
          filterName={scope}
          searchKey="name"
          searchValue={searchValue}
          placeholder="Search by name"
          loading={list.isLoading}
          refetching={list.isFetching}
          loadingLoadMore={list.isFetchingNextPage}
          hasNextPage={list.hasNextPage}
          onLoadMore={list.fetchNextPage}
          onRefresh={list.refetch}
          data={rows}
          totalData={count.data}
          renderItem={({ data, index }) => (
            <TouchableOpacity
              activeOpacity={0.6}
              style={styles.promotionItem}
              onPress={() => gotToDetail(data)}
              key={index}>
              <View style={[styles.promotionItemSection, { marginBottom: 10 }]}>
                <SociollaText type="medium" style={{ fontSize: 15 }}>
                  {data.name ? data.name : '-'}
                </SociollaText>
                <StatusBadge status={data.is_active ? 'active' : 'in_active'} />
              </View>
              <View style={styles.promotionItemSection}>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <Image
                    style={{
                      width: 19,
                      height: 14,
                      marginLeft: -5,
                      marginRight: 5,
                    }}
                    width={19}
                    height={14}
                    svg={true}
                    source={require('@assets/img/icons/menu/calendar.svg')}
                  />
                  <SociollaText style={{ fontSize: 11, color: Colors.grey2 }}>
                    {data.periode ? data.periode : '-'}
                  </SociollaText>
                </View>
                <SociollaText style={{ fontSize: 11, color: Colors.grey1 }}>
                  Type: {data.promotion_type}
                </SociollaText>
              </View>
            </TouchableOpacity>
          )}
        />
      )}
      <Layout.Filter
        name="promotion-rules"
        filterName={scope}
        isLocalVariable={true}
        hasFilterData={Object.keys(draftFilter).length > 0}
        onReset={() => {
          if (list.isFetching) {
            return;
          }
          setDraftFilter({});
          dispatch(paramsReseted({ key: scope }));
        }}
        onApply={() => {
          if (list.isFetching) {
            return;
          }
          dispatch(filterAdded({ key: scope, filter: draftFilter }));
          dispatch(showPopup({ name: 'showFilter', value: false }));
        }}>
        <Layout.FilterItem>
          <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
            Date
          </SociollaText>
          <View style={{ width: '100%', flexDirection: 'row' }}>
            <DatePicker
              label="From"
              name="from_date"
              style={{
                marginRight: isWeb ? 10 : 0,
                width: isWeb ? 'auto' : '50%',
              }}
              value={draftFilter.from_date?.$gte}
              onChange={(value) => draftDateHandler(value, '$gte')}
            />
            <DatePicker
              label="To"
              name="to_date"
              style={{ width: isWeb ? 'auto' : '50%' }}
              value={draftFilter.from_date?.$lte}
              disableBeforeDate={draftFilter.from_date?.$gte}
              onChange={(value) => draftDateHandler(value, '$lte')}
            />
          </View>
        </Layout.FilterItem>
        <Layout.FilterItem>
          <StatusPicker
            label="Promo Model"
            options={PROMO_MODEL_LIST}
            value={draftFilter['promotion_model']}
            trackBy="value"
            labelKey="name"
            minWidth={80}
            onChange={(val) =>
              setDraftFilter((f) => ({ ...f, promotion_model: val.value }))
            }
          />
        </Layout.FilterItem>
        <Layout.FilterItem>
          <StatusPicker
            label="Status"
            options={STATUS_LIST}
            value={draftFilter['current_status']}
            trackBy="value"
            labelKey="name"
            minWidth={80}
            onChange={(val) =>
              setDraftFilter((f) => ({ ...f, current_status: val.value }))
            }
          />
        </Layout.FilterItem>
        <Layout.FilterItem lastItem>
          <StatusPicker
            label="Is Active"
            options={[
              { name: 'Active', value: true },
              { name: 'Inactive', value: false },
            ]}
            value={draftFilter['is_active']}
            trackBy="value"
            labelKey="name"
            minWidth={100}
            onChange={(val) =>
              setDraftFilter((f) => ({ ...f, is_active: val.value }))
            }
          />
        </Layout.FilterItem>
      </Layout.Filter>

      {showPromotionRuleExport && (
        <ModalPromotionRuleExport show={showPromotionRuleExport} />
      )}
    </Layout.Container>
  );
};

const styles = StyleSheet.create({
  promotionItem: {
    width: '100%',
    paddingVertical: 10,
    paddingHorizontal: 10,
    backgroundColor: Colors.white,
    borderBottomWidth: 1,
    borderBottomColor: Colors.grey0,
  },
  promotionItemSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
});

export default PromotionRulesScreen;
